<template>
  <el-popover
    :disabled="!disabled"
    placement="top-start"
    trigger="hover"
    width="fit-content"
  >
    <template #default>
      <RequirementsLabel
        v-if="disabled"
        :type="requirement"
        :sid="item.key"
        @modal="onModal"
      />
    </template>
    <template #reference>
      <el-card
        :id="`sid-${item.key}`"
        shadow="hover"
        :class="{ disabled: disabled }"
        :body-style="{ padding: '0' }"
      >
        <template #header>
          <div class="flex-container">
            <span>Minimum payment</span>
            <b>{{ item.minimum_payment }} {{ currenciesTitle }}</b>
          </div>
        </template>
        <VueFlip
          v-model="flipped"
          :active-hover="false"
          class="flip-flop-body"
          @mouseenter="openForm"
          @mouseleave="closeForm"
        >
          <template #front>
            <div class="logo-wrapper">
              <img
                v-if="!loading"
                :src="logo"
                :class="{ disabled: disabled }"
                class="logo"
              />
              <svg
                v-if="item.cashback_enabled"
                class="cashback-badge"
                xmlns="http://www.w3.org/2000/svg"
                width="128"
                height="58"
                viewBox="0 0 128 58"
                role="img"
                aria-label="+1% cashback badge"
              >
                <defs>
                  <filter
                    id="softShadow"
                    x="-15%"
                    y="-15%"
                    width="130%"
                    height="130%"
                  >
                    <feDropShadow
                      dx="1.5"
                      dy="1.5"
                      stdDeviation="0.8"
                      flood-color="#000000"
                      flood-opacity="0.22"
                    />
                  </filter>
                </defs>

                <!-- original-style black cashback label, without right-edge artefacts -->
                <g
                  transform="translate(42 10) rotate(-2 42 19)"
                  filter="url(#softShadow)"
                >
                  <path
                    d="M8 1 H75 Q83 1 83 9 V30 Q83 38 75 38 H8 Q1 38 1 31 V8 Q1 1 8 1 Z"
                    fill="#101318"
                    stroke="#ffffff"
                    stroke-width="3"
                  />
                  <path
                    d="M10 6 H73 Q78 6 78 11 V28 Q78 33 73 33 H10 Q6 33 6 29 V10 Q6 6 10 6 Z"
                    fill="none"
                    stroke="#ffffff"
                    stroke-width="1.2"
                    opacity="0.75"
                  />

                  <text
                    x="43"
                    y="18"
                    text-anchor="middle"
                    font-family="Arial Black, Arial, Helvetica, sans-serif"
                    font-size="13"
                    font-weight="900"
                    font-style="italic"
                    fill="#ffffff"
                    letter-spacing="-.4"
                  >
                    CASH
                  </text>

                  <text
                    x="43"
                    y="30"
                    text-anchor="middle"
                    font-family="Arial Black, Arial, Helvetica, sans-serif"
                    font-size="13"
                    font-weight="900"
                    font-style="italic"
                    fill="#ffffff"
                    letter-spacing="-.4"
                  >
                    BACK
                  </text>
                </g>

                <!-- blue +X% circle -->
                <g transform="translate(8 7)" filter="url(#softShadow)">
                  <circle cx="22" cy="22" r="22" fill="#428AF7" />
                  <text
                    x="22"
                    y="28"
                    text-anchor="middle"
                    font-family="Arial Black, Arial, Helvetica, sans-serif"
                    font-size="15"
                    font-weight="900"
                    fill="#ffffff"
                  >
                    +{{ item.cashback_percent }}%
                  </text>
                </g>
              </svg>
            </div>
          </template>
          <template #back>
            <el-form
              v-if="!disabled"
              ref="form"
              :model="payment"
              :rules="rules"
              @submit.prevent
            >
              <el-form-item style="margin-bottom: 0">
                <strong>{{ item.title }}</strong>
              </el-form-item>
              <el-form-item :error="errors.amount" prop="amount">
                <el-popover
                  :model="popoverVisible"
                  :disabled="!popoverVisible"
                  trigger="hover"
                  width="fit-content"
                  placement="top-start"
                >
                  <template #default>
                    <el-tag type="danger">
                      {{ popoverTitle }}
                    </el-tag>
                  </template>
                  <template #reference>
                    <el-input
                      v-model.number="payment.amount"
                      :disabled="loading"
                      placeholder="100"
                      class="amount"
                    >
                      <template #append>
                        <el-button
                          :loading="loading"
                          type="primary"
                          icon="MoneyElementIcon"
                          @click="onSubmit"
                        >
                          {{ buttonText }}
                        </el-button>
                      </template>
                    </el-input>
                  </template>
                </el-popover>
              </el-form-item>
              <router-link
                v-if="item.required_verification === VerificationType.Identity"
                :to="`/payment/verification/${item.key}`"
                class="el-button el-button--medium verification-btn"
              >
                <el-icon>
                  <CircleCheckElementIcon />
                </el-icon>
                Verification status
              </router-link>
              <el-radio-group
                v-if="showCurrencySelect"
                v-model="payment.currency"
                size="large"
                @change="onCurrencySelect"
              >
                <el-radio-button
                  v-for="currency in item.currencies"
                  :key="currency"
                  :value="currency"
                  :label="currency"
                />
              </el-radio-group>
              <div v-if="cardRequests.length" class="active-requests">
                <div
                  v-for="request in cardRequests"
                  :key="request.id"
                  class="active-requests__row"
                  :class="{
                    'active-requests__row--clickable': request.attributes.has_crypto_address
                  }"
                  :role="request.attributes.has_crypto_address ? 'button' : undefined"
                  :tabindex="request.attributes.has_crypto_address ? 0 : undefined"
                  @click.stop="openRequest(request)"
                  @keydown.enter.prevent="openRequest(request)"
                  @keydown.space.prevent="openRequest(request)"
                >
                  <el-icon class="active-requests__warning">
                    <WarningFilledElementIcon />
                  </el-icon>
                  <span>Unpaid</span>
                  <span class="active-requests__amount">
                    {{
                      formatPaymentAmount(
                        request.attributes.amount,
                        request.attributes.payment_system_name,
                        request.attributes.currency_sign
                      )
                    }}
                  </span>
                  <span>{{ timeLeft(request.attributes.created_int) }} left</span>
                </div>
                <el-button
                  link
                  type="primary"
                  class="active-requests__link"
                  @click.stop="contextShowRequests"
                >
                  View requests
                </el-button>
              </div>
            </el-form>
          </template>
        </VueFlip>
        <div class="footer flex-container">
          <span>Fees</span>
          <el-badge
            v-if="item.online"
            value="Online"
            type="primary"
            class="online"
          />
          <el-badge v-else value="Invoice" type="success" class="online" />
          <b v-if="String(item.commission_title).length <= 16">{{
            item.commission_title
          }}</b>
          <template v-else>
            <div>
              <b>{{ item.commission_title.substring(0, 12) }}...</b>
              <IconWithHint>{{ item.commission_title }}</IconWithHint>
            </div>
          </template>
        </div>
      </el-card>
    </template>
  </el-popover>
</template>

<script lang="ts">
import { defineComponent, computed, ref, type PropType } from 'vue'
import { VueFlip } from 'vue-flip'
import useValidation from '@/hooks/useValidation'
import { PaymentValidationErrorsKey, VerificationType } from '@/api/types'
import RequirementsLabel from './RequirementsLabel.vue'
import IconWithHint from '@/components/IconWithHint.vue'
import { ElMessageBox } from 'element-plus'
import type { IReplenishment } from '@/interfaces/replenishments'
import { formatPaymentAmount } from '../useActivePaymentRequests'

type CardRequest = IReplenishment & {
  attributes: IReplenishment['attributes'] & {
    payment_system_sid: string | null
    has_crypto_address: boolean
    currency_sign?: string
  }
}

export default defineComponent({
  name: 'PaymentMethodCard',
  components: {
    VueFlip,
    RequirementsLabel,
    IconWithHint
  },
  props: {
    item: {
      type: Object,
      required: true
    },
    disabled: {
      type: Boolean,
      required: true
    },
    requirement: {
      type: String,
      required: false
    },
    vatPayer: {
      type: Boolean,
      default: false
    },
    confirmationData: {
      type: Object,
      default: () => null
    },
    isStaff: {
      type: Boolean,
      default: false
    },
    activeRequests: {
      type: Array as PropType<CardRequest[]>,
      default: () => []
    },
    now: {
      type: Number,
      required: true
    }
  },
  setup(props, context) {
    const { validationErrors } = useValidation(PaymentValidationErrorsKey)
    const errors = validationErrors()
    const cardRequests = computed(() =>
      props.activeRequests
        .filter(
          (request) => request.attributes.payment_system_sid === props.item.key
        )
        .slice(0, 3)
    )
    const timeLeft = (createdAt: number) => {
      const seconds = Math.max(
        0,
        Math.ceil((createdAt * 1000 + 86400000 - props.now) / 1000)
      )
      const hours = Math.floor(seconds / 3600)
      const minutes = Math.floor((seconds % 3600) / 60)
      return `${hours}h ${String(minutes).padStart(2, '0')}m`
    }
    const contextShowRequests = () => context.emit('show-requests')
    const openRequest = (request: CardRequest) => {
      if (request.attributes.has_crypto_address) {
        context.emit('view-request', request.id)
      }
    }

    const rules = computed(() => {
      const baseRules: any = {
        amount: [
          { required: true, message: 'Please input amount', trigger: 'blur' }
        ]
      }

      if (!props.isStaff) {
        baseRules.amount.push({
          type: 'number',
          min: Number(props.item.minimum_payment),
          max: Number(props.item.maximum_payment),
          message: `Amount should be between ${props.item.minimum_payment} and ${props.item.maximum_payment}`
        })
      } else {
        baseRules.amount.push({
          type: 'number',
          max: Number(props.item.maximum_payment),
          message: `Amount should be less than ${props.item.maximum_payment}`
        })
      }

      return baseRules
    })

    const loading = ref(false)
    const flipped = ref(false)
    const form = ref(null)

    const firstCurrency = props.item.currencies[0]

    const payment = ref({
      id: props.item.currency_to_id[firstCurrency],
      amount: null,
      cents: 0,
      currency: firstCurrency
    })

    const logo = computed(() => {
      return (
        props.item.logo ||
        `https://dummyimage.com/230x150/1890ff/fff.png&text=${props.item.title}`
      )
    })

    const buttonText = computed(() => {
      return props.item.online ? 'Pay now' : 'Request invoice'
    })

    const popoverTitle = computed(() => {
      if (props.vatPayer && props.item.key === 'paypal') {
        return 'Please note that you will be charged 20% VAT as you are a resident of the European Union'
      }

      return ''
    })

    const popoverVisible = computed(() => {
      return popoverTitle.value.length > 0 && !props.disabled
    })

    const doSubmit = () => {
      flipped.value = false
      loading.value = true
      context.emit(
        'submit',
        { ...payment.value, cents: Number(payment.value.amount) * 100 },
        loading,
        true
      )
    }

    const showCurrencySelect = computed(() => {
      return props.item.currencies.length > 1
    })

    const currenciesTitle = computed(() => {
      return props.item.currencies.join('/')
    })

    const onCurrencySelect = (currency: string) => {
      const methodId = props.item.currency_to_id[currency]
      payment.value.id = methodId
    }

    const openForm = () => {
      if (!(loading.value || props.disabled)) {
        flipped.value = true
      }
    }

    const closeForm = () => {
      flipped.value = false
    }

    const onSubmit = () => {
      // @ts-ignore: Object is possibly 'null'.
      form.value.validate((valid: boolean) => {
        if (valid) {
          if (
            props.confirmationData &&
            Object.prototype.hasOwnProperty.call(
              props.confirmationData,
              'description'
            )
          ) {
            ElMessageBox.confirm(
              props.confirmationData.description,
              props.confirmationData.title,
              {
                confirmButtonText: 'Continue payment',
                cancelButtonText: 'Cancel',
                type: 'warning'
              }
            )
              .then(() => {
                doSubmit()
              })
              .catch(() => {
                // do nothing
              })
          } else {
            doSubmit()
          }
        } else {
          return false
        }
      })
    }

    const onModal = () => {
      context.emit('modal')
    }

    return {
      onSubmit,
      onCurrencySelect,
      logo,
      payment,
      flipped,
      rules,
      form,
      errors,
      loading,
      VerificationType,
      popoverTitle,
      popoverVisible,
      showCurrencySelect,
      currenciesTitle,
      buttonText,
      onModal,
      openForm,
      closeForm,
      cardRequests,
      timeLeft,
      contextShowRequests,
      openRequest,
      formatPaymentAmount
    }
  }
})
</script>

<style lang="scss" scoped>
.flex-container {
  display: flex;
  justify-content: space-between;
}
:deep(.el-card__header) {
  padding: 10px;
}
.footer {
  padding: 15px 20px 0 20px;
  border-top: 1px solid #e6ebf5;

  :deep(.el-badge__content) {
    top: 0;
  }
}
.flip-flop-body {
  height: 150px;
  display: flex;
  justify-content: center;
  align-items: center;
  text-align: center;
}
.active-requests {
  font-size: 12px;
  margin-top: 8px;
}
.active-requests__row {
  display: flex;
  align-items: center;
  gap: 4px;
  color: #707070;
  white-space: nowrap;
  margin: 2px 0 2px 0;
}
.active-requests__warning {
  color: var(--el-color-warning);
}
.active-requests__row--clickable {
  cursor: pointer;
}
.active-requests__amount {
  flex: 1;
}
.active-requests__link {
  display: flex;
  margin-left: auto;
  font-size: 10px;
  text-decoration: underline;
}
.el-form {
  margin: 0 20px;
}
.el-card {
  padding-bottom: 10px;

  &:hover {
    cursor: pointer;
  }

  &.disabled {
    opacity: 0.4;
  }
}
.el-input-group__append button.el-button {
  color: #ffffff;
  background-color: #409eff;
  border-color: #409eff;
}
.red {
  color: red;
}
.verification-btn {
  vertical-align: middle;
  margin-right: 10px;
}
.logo {
  display: block;
  width: auto;
  height: auto;
  max-width: 100%;
  max-height: 100%;
  object-fit: contain;

  &.disabled {
    filter: grayscale(1);
  }
}

.logo-wrapper {
  position: relative;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  height: 100%;
  max-width: 100%;
}

.cashback-badge {
  position: absolute;
  top: -5px;
  right: -10%;
  z-index: 10;
}
</style>
